import { useState, useEffect } from 'react';
import type { CreateProjectData, UpdateProjectData, Project } from '../../api/projects';

interface ProjectFormProps {
  project?: Project;
  onSubmit: (data: CreateProjectData | UpdateProjectData) => Promise<void>;
  onCancel: () => void;
  isEdit?: boolean;
}

export function ProjectForm({ project, onSubmit, onCancel, isEdit = false }: ProjectFormProps) {
  const [projectId, setProjectId] = useState(project?.projectId ?? '');
  const [name, setName] = useState(project?.name ?? '');
  const [description, setDescription] = useState(project?.description ?? '');
  const [teamId, setTeamId] = useState(project?.teamId ?? '');
  const [owner, setOwner] = useState(project?.owner ?? '');
  const [maxBudget, setMaxBudget] = useState(project?.maxBudget?.toString() ?? '');
  const [tpmLimit, setTpmLimit] = useState(project?.tpmLimit?.toString() ?? '');
  const [rpmLimit, setRpmLimit] = useState(project?.rpmLimit?.toString() ?? '');
  const [allowedCidrs, setAllowedCidrs] = useState(project?.allowedCidrs?.join(', ') ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (project) {
      setProjectId(project.projectId);
      setName(project.name);
      setDescription(project.description);
      setTeamId(project.teamId ?? '');
      setOwner(project.owner);
      setMaxBudget(project.maxBudget?.toString() ?? '');
      setTpmLimit(project.tpmLimit?.toString() ?? '');
      setRpmLimit(project.rpmLimit?.toString() ?? '');
      setAllowedCidrs(project.allowedCidrs?.join(', ') ?? '');
    }
  }, [project]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      const cidrs = allowedCidrs
        .split(',')
        .map((c) => c.trim())
        .filter(Boolean);

      if (isEdit) {
        const data: UpdateProjectData = {
          name,
          description,
          teamId: teamId || undefined,
          owner,
          maxBudget: maxBudget ? parseFloat(maxBudget) : undefined,
          tpmLimit: tpmLimit ? parseInt(tpmLimit, 10) : undefined,
          rpmLimit: rpmLimit ? parseInt(rpmLimit, 10) : undefined,
          allowedCidrs: cidrs.length > 0 ? cidrs : undefined,
        };
        await onSubmit(data);
      } else {
        const data: CreateProjectData = {
          projectId,
          name,
          description,
          teamId: teamId || undefined,
          owner,
          maxBudget: maxBudget ? parseFloat(maxBudget) : undefined,
          tpmLimit: tpmLimit ? parseInt(tpmLimit, 10) : undefined,
          rpmLimit: rpmLimit ? parseInt(rpmLimit, 10) : undefined,
          allowedCidrs: cidrs.length > 0 ? cidrs : undefined,
        };
        await onSubmit(data);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save project');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      {error && (
        <div className="alert alert--error" style={{ marginBottom: 16 }}>
          <span>{error}</span>
        </div>
      )}

      {!isEdit && (
        <div className="modal__field">
          <label htmlFor="project-id">Project ID (slug)</label>
          <input
            id="project-id"
            type="text"
            value={projectId}
            onChange={(e) => setProjectId(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
            placeholder="my-project"
            required
            autoFocus
          />
        </div>
      )}

      <div className="modal__field">
        <label htmlFor="project-name">Project Name</label>
        <input
          id="project-name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="My LLM Project"
          required
        />
      </div>

      <div className="modal__field">
        <label htmlFor="project-desc">Description</label>
        <textarea
          id="project-desc"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Brief description of the project..."
          rows={3}
        />
      </div>

      <div className="modal__field">
        <label htmlFor="project-team">Team ID</label>
        <input
          id="project-team"
          type="text"
          value={teamId}
          onChange={(e) => setTeamId(e.target.value)}
          placeholder="platform-team"
        />
      </div>

      <div className="modal__field">
        <label htmlFor="project-owner">Owner Email</label>
        <input
          id="project-owner"
          type="email"
          value={owner}
          onChange={(e) => setOwner(e.target.value)}
          placeholder="owner@example.com"
          required
        />
      </div>

      <div className="modal__field">
        <label htmlFor="project-budget">Max Budget ($)</label>
        <input
          id="project-budget"
          type="number"
          step="0.01"
          min="0"
          value={maxBudget}
          onChange={(e) => setMaxBudget(e.target.value)}
          placeholder="500.00"
        />
      </div>

      <div className="modal__field">
        <label htmlFor="project-tpm">TPM Limit</label>
        <input
          id="project-tpm"
          type="number"
          min="0"
          value={tpmLimit}
          onChange={(e) => setTpmLimit(e.target.value)}
          placeholder="500000"
        />
      </div>

      <div className="modal__field">
        <label htmlFor="project-rpm">RPM Limit</label>
        <input
          id="project-rpm"
          type="number"
          min="0"
          value={rpmLimit}
          onChange={(e) => setRpmLimit(e.target.value)}
          placeholder="120"
        />
      </div>

      <div className="modal__field">
        <label htmlFor="project-cidrs">Allowed CIDRs (comma-separated)</label>
        <input
          id="project-cidrs"
          type="text"
          value={allowedCidrs}
          onChange={(e) => setAllowedCidrs(e.target.value)}
          placeholder="10.0.0.0/8, 172.16.0.0/12"
        />
      </div>

      <div className="modal__actions">
        <button type="button" className="modal__cancel" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="modal__submit" disabled={submitting}>
          {submitting ? 'Saving...' : isEdit ? 'Update Project' : 'Create Project'}
        </button>
      </div>
    </form>
  );
}
